import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { XIcon, PlusIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';

interface SplitConfigurationUIProps {
  splits: Array<{ value: string; percentage: number }>;
  options: SelectDropdownOption[];
  onChange: (splits: Array<{ value: string; percentage: number }>) => void;
  onSwitchToSimple: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export function SplitConfigurationUI({
  splits,
  options,
  onChange,
  onSwitchToSimple,
  loading = false,
  disabled = false,
}: SplitConfigurationUIProps) {
  const gt = useGT();
  const totalPercentage = splits.reduce((sum, s) => sum + s.percentage, 0);

  // Validation with tolerance:
  // - Green (valid): 99.99 - 100.01 (within 0.01 tolerance)
  // - Yellow (warning): 99.90 - 99.99 or 100.01 - 100.10 (slight deviation)
  // - Red (invalid): < 99.90 or > 100.10 (major deviation)
  const deviation = Math.abs(totalPercentage - 100);
  const isValid = deviation <= 0.01;
  const isWarning = deviation > 0.01 && deviation <= 0.1;

  // Get disabled option IDs for each split (options used in OTHER splits)
  const getDisabledOptionIds = React.useCallback(
    (currentIndex: number): string[] => {
      const usedValues = splits
        .map((s, i) => (i !== currentIndex ? s.value?.trim() : null))
        .filter((v) => v);

      // Map values back to option IDs
      return options
        .filter((opt) => usedValues.includes(opt.display.trim()))
        .map((opt) => opt.id);
    },
    [splits, options],
  );

  const handleSplitUpdate = (
    index: number,
    field: 'value' | 'percentage',
    newValue: string | number,
  ) => {
    const newSplits = [...splits];
    if (field === 'value') {
      newSplits[index] = { ...newSplits[index], value: newValue as string };
    } else {
      newSplits[index] = {
        ...newSplits[index],
        percentage: parseFloat(newValue as string) || 0,
      };
    }
    onChange(newSplits);
  };

  const handleRemoveSplit = (index: number) => {
    onChange(splits.filter((_, i) => i !== index));
  };

  const handleAddSplit = () => {
    onChange([...splits, { value: '', percentage: 0 }]);
  };

  const handleDistributeEvenly = () => {
    const count = splits.length;
    const evenSplit = parseFloat((100 / count).toFixed(3));

    onChange(
      splits.map((s) => ({
        ...s,
        percentage: evenSplit,
      })),
    );
  };

  const handleDistributeAll = () => {
    if (options.length === 0) return;

    const count = options.length;
    const evenSplit = parseFloat((100 / count).toFixed(3));

    const newSplits = options.map((option) => ({
      value: option.display,
      percentage: evenSplit,
    }));

    onChange(newSplits);
  };

  const totalStatusMessage = isValid
    ? gt('✓', { context: 'split total valid, checkmark' })
    : isWarning
      ? gt('⚠ Close to 100% (acceptable)', {
          context: 'split total warning, nearly 100 percent',
        })
      : gt('⚠ Must equal 100%', {
          context: 'split total error, must sum to 100 percent',
        });

  const findOption = (value: string): SelectDropdownOption | null => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    return (
      options.find((o) => o.display.trim().toLowerCase() === normalized) || null
    );
  };

  return (
    <div className="border rounded p-3 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          <T context="heading for multi-value split percentage UI">
            Split Configuration
          </T>
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDistributeAll}
            disabled={disabled || options.length === 0}
            className="text-xs"
          >
            <T context="button to create splits for all options">
              Distribute All
            </T>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDistributeEvenly}
            disabled={disabled || splits.length === 0}
            className="text-xs"
          >
            <T context="button to split percentages evenly across rows">
              Distribute Evenly
            </T>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSwitchToSimple}
            disabled={disabled}
          >
            <T context="button to switch to simple split mode">⚡ Use Simple</T>
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {splits.map((split, index) => {
          const isEmpty = !split.value || split.value.trim() === '';
          const disabledOptionIds = getDisabledOptionIds(index);

          return (
            <div key={index} className="p-3 bg-white rounded border space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <SelectDropdown
                    options={options}
                    value={findOption(split.value)}
                    onChange={(selectedOption) => {
                      handleSplitUpdate(
                        index,
                        'value',
                        selectedOption?.display || '',
                      );
                    }}
                    placeholder={gt('Select value...', {
                      context: 'split row dropdown placeholder',
                    })}
                    size="small"
                    disabled={disabled || loading}
                    error={isEmpty}
                    helperText={
                      isEmpty
                        ? gt('Please select a value', {
                            context: 'validation, split row requires value',
                          })
                        : undefined
                    }
                    fullWidth={true}
                    disabledOptionIds={disabledOptionIds}
                    disabledHelperText={gt('Already used in another split', {
                      context: 'helper when split option disabled as duplicate',
                    })}
                  />
                </div>
                <div className="w-28">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.001"
                    value={split.percentage || ''}
                    onChange={(e) =>
                      handleSplitUpdate(index, 'percentage', e.target.value)
                    }
                    placeholder={gt('0.000', {
                      context: 'numeric placeholder for percentage',
                    })}
                    disabled={disabled}
                    className="text-right"
                  />
                </div>
                <span className="text-sm text-gray-500 mt-2">
                  {gt('%', { context: 'percent symbol suffix' })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRemoveSplit(index)}
                  disabled={disabled || splits.length === 1}
                  className="flex-shrink-0"
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center gap-3 px-1">
                <Slider
                  value={[split.percentage || 0]}
                  onValueChange={(value) =>
                    handleSplitUpdate(index, 'percentage', value.toString())
                  }
                  max={100}
                  step={0.5}
                  disabled={disabled}
                  className="flex-1"
                />
                <span className="text-xs text-gray-500 w-14 text-right">
                  {split.percentage?.toFixed(3) || '0.000'}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        size="sm"
        variant="outline"
        onClick={handleAddSplit}
        disabled={disabled}
        className="w-full"
      >
        <PlusIcon className="w-4 h-4 mr-1" />
        <T context="button to add another split row">Add Split</T>
      </Button>

      <div
        className={`text-sm font-medium text-center p-2 rounded ${
          isValid
            ? 'text-green-700 bg-green-50'
            : isWarning
              ? 'text-yellow-700 bg-yellow-50'
              : 'text-red-700 bg-red-50'
        }`}
      >
        <T context="split configuration total percentage with validation status">
          Total: <Var>{totalPercentage.toFixed(3)}</Var>%{' '}
          <Var>{totalStatusMessage}</Var>
        </T>
      </div>
    </div>
  );
}
